import React, { Component } from "react";

import PMDecisionsRevenueForecast from "../components/pm/decisions/PMDecisionsRevenueForecast";
import PMDecisionsPromotionalSpend from "../components/pm/decisions/PMDecisionsPromotionalSpend";
import PMDecisionsPlacement from "../components/pm/decisions/PMDecisionsPlacement";
import ManagePromotionPage from "../components/pm/decisions/manage-promotion-page";
import SetPricingPage from "../components/pm/decisions/set-pricing-page";
import ManagePlacementPage from "../components/pm/decisions/manage-placement-page";
import { Loader } from "react-loaders";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import { fetchTeamDecisions, fetchTeamStatus } from "../api/TeamDecisionsService";
import Auth from "../routes/Auth";
import RoundNotStartedGate from "../components/round-not-started";
import FirstDecisionModal from "../components/first-decision-modal";
import { submitDecisions } from "../api/SubmitDecisions";
import SubmitDecisionsButton from "../components/ui-building-blocks/buttons/submit-decisions";
import EventEmitter from "../services/event-emitter";
import { logError, detectMobile } from "../services/utils";
import RoleButtons from "../components/role-buttons";
import OpsStyled from "components/operations/style";
import FinanceStyled from "components/finance/style";
import Teams from "api/Teams";
import { view } from "@risingstack/react-easy-state";
import LastStepModal from '../components/get-started/last-step';
import SuccessAlert from "../components/alerts/success";
import ErrorAlert from "../components/alerts/error";
import styled from "styled-components";

// Promo breakdown colors. Main category colors anchor the donut slices
// and the category-row bullet. Each palette entry is a similar-but-
// distinct shade applied to the sub-category (per-client) bullets in
// order; the palettes wrap if the backend ever returns more channels
// per category than we have shades for.
const TRADITIONAL_COLOR = '#4CC7D1';
const DIGITAL_COLOR = '#4C8AFF';
const TRADITIONAL_PALETTE = ['#4CC7D1', '#5FD0D9', '#7BDAE1', '#38B5C0', '#2AA1AB'];
const DIGITAL_PALETTE = ['#4C8AFF', '#6FA0FF', '#88B5FF', '#3A7AE8', '#2865D6'];

// Wrapper + tooltip for the disabled "Make Decisions" button when no
// products exist yet. Mirrors the AddProductTooltip pattern in
// PDDecisions.js — pointer-events on the child button are killed so
// the wrapper's :hover reliably fires.
const MakeDecisionsTooltipWrapper = styled.span`
  position: relative;
  display: inline-flex;

  &:hover > span.make-decisions-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const MakeDecisionsTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 0;
  transform: translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 320px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 10;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 24px;
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

class PMDecisions extends Component {
  constructor(props) {
    super(props);

    this._isMounted = false;

    let role = Auth.getRole();
    let has_access = role === "CEO" || role === "PM";
    let isOwnRole = role === "PM";

    this.state = {
      marketing_clients: {},
      marketing_clients_list: [],
      sales_clients: {},
      sales_clients_list: [],
      sales: [],
      promos: [],
      price_forecasts: [],
      products: [],
      accountsReceivable: 0,
      loaded: false,
      hasAccess: has_access,
      isOwnRole,
      roleAlert: false,
      currentRound: props.currentRound,
      roundInUrl: props.roundInUrl,
      rounds: props.rounds,
      submission_data: {
        pm: {
          date: "N/A",
          unaccepted: 0
        }
      },
      submitDecisionsLoading: false,
      showNoProductsModal: false,
      isPreChecked: false,
      successMessage: null,
      showMakeDecisionsModal: false,
      showManagePromotionPage: false,
      showSetPricingPage: false,
      showManagePlacementPage: false,
      // First-time coaching popup after the user sets their first
      // pricing in Round 1. See handleFirstPricingSet below.
      firstDecisionModalOpen: false,
      // Same idea for the first promo (Set Allocation) decision in
      // Round 1. See handleFirstPromoSet below.
      firstPromoModalOpen: false,
      // Promo Spend Breakdown filter — 'all' or a product id. Drives
      // which slice of promos.spend feeds the donut + per-channel list.
      promoBreakdownProductId: 'all',
    };

    this.eventemitter = EventEmitter.getInstance();
    this.isMobile = detectMobile();
  }

  showSuccessMessage = successMessage => {
    this.setState({
      successMessage,
    }, () => {
      this.hideSuccessMessage();
    });
  }

  hideSuccessMessage = () => {
    setTimeout(() => {
      this.setState({
        successMessage: null,
      });
    }, 5000);
  }

  hideErrorMessage = () => {
    this.setState({
      errorMessage: null,
    });
  }

  hideNoProductsModal = () => {
    this.setState({
      showNoProductsModal: false,
    });
  }

  reloadRole = () => {
    const role = Auth.getRole();
    const hasAccess = role === "CEO" || role === "PM";
    const isOwnRole = role === "PM";

    this.setState({
      hasAccess,
      isOwnRole
    });
  };

  componentWillReceiveProps(nextProps) {
    if (
      this.state.roundInUrl !== nextProps.roundInUrl ||
      this.state.currentRound !== nextProps.currentRound ||
      (this.state.rounds &&
        this.state.rounds.length === 0 &&
        nextProps.rounds &&
        nextProps.rounds.length !== 0)
    ) {
      this.setState(
        {
          roundInUrl: nextProps.roundInUrl,
          currentRound: nextProps.currentRound,
          rounds: nextProps.rounds
        },
        () => {
          this.fetchData();
          this.reloadRole();
        }
      );
    }
  }

  showRoleAlert = () => {
    this.setState({
      roleAlert: true
    });
  };

  hideRoleAlert = () => {
    this.setState({
      roleAlert: false
    });
  };

  submitDecisions = () => {
    this.setState({
      submitDecisionsLoading: true
    });
    submitDecisions("marketing", this.roundNumber, (showWarnings) => this.getSubmissionData(showWarnings));
  };

  get roundNumber() {
    const { roundInUrl, currentRound } = this.state;

    if (roundInUrl !== null && roundInUrl !== currentRound) {
      return roundInUrl;
    }

    return currentRound;
  }

  fetchData = () => {
    const currentRound = this.roundNumber;

    Promise.all([
      fetchTeamDecisions(currentRound, 'marketing'),
      fetchTeamStatus(currentRound),
    ])
      .then(([decisionsData, statusData]) => {
        if (!this._isMounted) return;

        let marketing_clients = {};
        let sales_clients = {};
        let sales = decisionsData.sales || [];
        let promos = decisionsData.promos || [];
        let price_forecasts = decisionsData.price_forecasts || [];
        let products = decisionsData.products || [];
        let balanceSheet = decisionsData.balanceSheet || [];
        const pmHasPricing = (decisionsData.price_forecasts || []).some(i => i.player !== null);
        const pmHasPromos = (decisionsData.promos || []).some(i => i.player !== null);
        const pmHasSales = (decisionsData.sales || []).some(i => i.player !== null);
        const isPreChecked = pmHasPricing && pmHasPromos && pmHasSales;

        const { rounds } = this.state;
        const round = rounds.find(i => i.number === currentRound);

        if (round) {
          sales = sales.filter(i => i.round === round.id);
          promos = promos.filter(i => i.round === round.id);
          price_forecasts = price_forecasts.filter(i => i.round === round.id);
          products = products.filter(i => i.round === round.id);
          balanceSheet = balanceSheet.filter(i => i.round === round.id);
        }

        // marketing_clients_list comes back from team-decisions with the
        // category field (1 = Traditional, 2 = Digital).
        const marketing_clients_list = decisionsData.marketing_clients || [];

        marketing_clients_list.forEach(item => {
          marketing_clients[item.name] = item.id;
        });

        // Rename the raw sales-client names to their UX-friendly labels
        // ("SalesRep" → "Direct Sales", "Distributor1" → "Distributors")
        // and keep both the {name: id} map and the rich array around.
        // The Manage Placement popup needs the full array to render one
        // option per client.
        const sales_clients_list = (decisionsData.sales_clients || []).map(item => {
          let { name } = item;
          if (name === "SalesRep") name = "Direct Sales";
          else if (name === "Distributor1") name = "Distributors";
          return { ...item, name };
        });
        sales_clients_list.forEach(item => {
          sales_clients[item.name] = item.id;
        });

        let accountsReceivable = 0;
        if (
          currentRound > 1 &&
          balanceSheet.length > 0 &&
          "Accounts Receivable" in balanceSheet[0]
        ) {
          accountsReceivable = balanceSheet[0]["Accounts Receivable"];
        }

        this.setState({
          marketing_clients,
          marketing_clients_list,
          sales_clients,
          sales_clients_list,
          sales,
          promos,
          price_forecasts,
          products,
          accountsReceivable,
          loaded: true,
          isPreChecked,
          submission_data: statusData.submission_data,
          submitDecisionsLoading: false,
        });
      })
      .catch(err => {
        logError(err, "PMDecisions", "fetchData");
      });
  };

  getSubmissionData = (showWarnings) => {
    this.setState({
      submitDecisionsLoading: true
    });

    Promise.all([
      fetchTeamDecisions(this.roundNumber, 'marketing'),
      fetchTeamStatus(this.roundNumber),
    ]).then(([decisionsData, statusData]) => {
      if (!this._isMounted) return;

      if (showWarnings) {
        showWarnings(decisionsData);
      }

      this.setState({
        submission_data: statusData.submission_data,
        submitDecisionsLoading: false
      });
    });
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentWillUnmount() {
    this._isMounted = false;

    if (this.products_timer) {
      clearTimeout(this.products_timer);
      this.products_timer = null;
    }
  }

  get actionsAllowed() {
    if (this.state.hasAccess === false) {
      return false;
    }

    if (this.props.roundShouldBeFinished === true) {
      return false;
    }

    const { roundInUrl } = this.state;
    const { currentRound, rounds } = this.props;

    if (roundInUrl !== null && roundInUrl !== currentRound) {
      return false;
    }

    const round = rounds.find(i => i.number === currentRound);

    if (!round) {
      return false;
    }

    return round.is_started === true && round.is_finished === false;
  }

  get countOfProducts() {
    return this.state.products.length;
  }

  showSetPricingModal = () => {
    const { products } = this.state;

    const hasProducts = (products && products.length > 0) ||
      (this.state.price_forecasts && this.state.price_forecasts.length > 0);
    if (!hasProducts) {
      this.setState({
        showNoProductsModal: true,
      });
    } else {
      this.eventemitter.trigger("pm-decisions-open-modal");
    }
  };

  showSetPromoBudgetModal = () => {
    this.eventemitter.trigger("pm-decisions-open-modal2");
  };

  get isSetPromoBudgetDisabled() {
    return (
      this.state.price_forecasts.filter(i => i.player !== null).length === 0
    );
  }

  showSetSalesChannelModal = () => {
    this.eventemitter.trigger("pm-decisions-open-modal3");
  };

  get isSetSalesChannelDisabled() {
    return (
      this.state.price_forecasts.filter(i => i.player !== null).length === 0
    );
  }

  openMakeDecisionsModal = () => {
    this.setState({ showMakeDecisionsModal: true });
  };

  closeMakeDecisionsModal = () => {
    this.setState({ showMakeDecisionsModal: false });
  };

  // Fired by SetPricingPage after a successful save when the save was
  // the user's first-ever pricing decision in Round 1. Shows the
  // "you have more decisions to make" coaching modal.
  handleFirstPricingSet = () => {
    this.setState({ firstDecisionModalOpen: true });
  };

  handleFirstDecisionClose = () => {
    this.setState({ firstDecisionModalOpen: false });
  };

  // CTA: close the popup, close the Set Pricing page, and open the
  // Make Decisions modal so the user sees the other two marketing
  // decisions they still need to make.
  handleFirstDecisionCta = () => {
    this.setState({
      firstDecisionModalOpen: false,
      showSetPricingPage: false,
      showMakeDecisionsModal: true,
    });
  };

  // First promo (Set Allocation) — fired from ManagePromotionPage
  // after a successful save when the team had no prior promo record
  // AND we're in Round 1. Reminds the student they still owe a Sales
  // Channel decision.
  handleFirstPromoSet = () => {
    this.setState({ firstPromoModalOpen: true });
  };

  handleFirstPromoModalClose = () => {
    this.setState({ firstPromoModalOpen: false });
  };

  // CTA: close the popup, close Manage Promotion, and open Manage
  // Placement (Sales Channel) directly — that's the "last marketing
  // decision" the popup copy tells them about.
  handleFirstPromoModalCta = () => {
    this.setState({
      firstPromoModalOpen: false,
      showManagePromotionPage: false,
      showManagePlacementPage: true,
    });
  };

  pickMakeDecisionsOption = (option) => () => {
    this.closeMakeDecisionsModal();
    if (option === 'pricing') {
      // Block opening the new page when no products are available yet
      // — preserves the existing UX hint that the user needs to talk
      // to Product Development first.
      const hasProducts = (this.state.products && this.state.products.length > 0)
        || (this.state.price_forecasts && this.state.price_forecasts.length > 0);
      if (!hasProducts) {
        this.setState({ showNoProductsModal: true });
        return;
      }
      this.setState({ showSetPricingPage: true });
    } else if (option === 'promotions') {
      this.setState({ showManagePromotionPage: true });
    } else if (option === 'placement') {
      this.setState({ showManagePlacementPage: true });
    }
  };

  closeManagePromotionPage = () => {
    this.setState({ showManagePromotionPage: false });
  };

  closeSetPricingPage = () => {
    this.setState({ showSetPricingPage: false });
  };

  closeManagePlacementPage = () => {
    this.setState({ showManagePlacementPage: false });
  };

  // ----- KPI / chart getters -----

  // Returns true if the promo row matches `productFilter`. 'all' (or
  // null) means every product; otherwise only the matching product's
  // promos count. Used by the Promotion Spend Breakdown section to
  // filter by selected product without affecting the top KPI row.
  matchesProductFilter = (promo, productFilter) => {
    if (productFilter == null || productFilter === 'all') return true;
    return Number(promo.product) === Number(productFilter);
  };

  // Promo entries come back signed-negative for outflows. Math.abs gives
  // the dollar amount the user actually spent.
  sumByCategory = (category, productFilter) => {
    const ids = (this.state.marketing_clients_list || [])
      .filter(c => Number(c.category) === category)
      .map(c => Number(c.id));
    if (ids.length === 0) return 0;
    return (this.state.promos || [])
      .filter(p => p.player !== null
        && ids.includes(Number(p.client))
        && this.matchesProductFilter(p, productFilter))
      .reduce((s, p) => s + Math.abs(Number(p.spend) || 0), 0);
  };

  // Per-client breakdown for the sub-lists under each category header.
  // Each entry carries a bullet color drawn from `palette` (wrapping)
  // so multiple sub-categories get visually similar but distinct dots.
  clientsByCategory = (category, palette, productFilter) => {
    return (this.state.marketing_clients_list || [])
      .filter(c => Number(c.category) === category)
      .map((client, i) => {
        const spend = (this.state.promos || [])
          .filter(p => p.player !== null
            && Number(p.client) === Number(client.id)
            && this.matchesProductFilter(p, productFilter))
          .reduce((s, p) => s + Math.abs(Number(p.spend) || 0), 0);
        return {
          id: client.id,
          name: client.name,
          spend,
          color: palette[i % palette.length],
        };
      });
  };

  // Top KPI getters always sum across ALL products — the breakdown
  // filter only affects the Promotion Spend Breakdown card itself.
  get sumTraditionalSpend() {
    return this.sumByCategory(1, 'all');
  }

  get sumDigitalSpend() {
    return this.sumByCategory(2, 'all');
  }

  selectPromoBreakdownProduct = (id) => () => {
    if (this.state.promoBreakdownProductId === id) return;
    this.setState({ promoBreakdownProductId: id });
  };

  get sumDirectSalesCost() {
    const dsId = this.state.sales_clients["Direct Sales"];
    return this.state.sales
      .filter(s => s.player !== null && s.client === dsId)
      .reduce((sum, s) => sum + Math.abs(Number(s.total) || 0), 0);
  }

  get sumDistributorsCost() {
    const distId = this.state.sales_clients["Distributors"];
    return this.state.sales
      .filter(s => s.player !== null && s.client === distId)
      .reduce((sum, s) => sum + Math.abs(Number(s.total) || 0), 0);
  }

  get totalPromoSpend() {
    return this.sumTraditionalSpend + this.sumDigitalSpend;
  }

  get totalMarketingSpend() {
    return this.totalPromoSpend + this.sumDirectSalesCost + this.sumDistributorsCost;
  }

  get avgPrice() {
    const priced = this.state.price_forecasts
      .filter(p => p.player !== null && Number(p.price) > 0);
    if (priced.length === 0) return 0;
    return priced.reduce((s, p) => s + Number(p.price), 0) / priced.length;
  }

  get placementCount() {
    return this.state.sales
      .filter(s => s.player !== null && Number(s.total) > 0)
      .length;
  }

  // Sum of sales.total across every live sales row in the round. Shown
  // in the Placement KPI card as a dollar figure.
  get sumPlacement() {
    return (this.state.sales || [])
      .filter(s => s.player !== null)
      .reduce((sum, s) => sum + Math.abs(Number(s.total) || 0), 0);
  }

  // ----- Per-product table data -----

  get productsRows() {
    const { products, price_forecasts, sales, sales_clients } = this.state;
    return products.map(product => {
      const pf = price_forecasts.find(i => i.product === product.id) || {};
      const price = Number(pf.price) || 0;
      const forecast = Number(pf.forecast) || 0;
      const revenue = Number(pf.revenue_forecast) || (price * forecast);

      // Sales channel: pick the sales row tied to this product (sales
      // rows reference product via the price_forecast they're associated
      // with). Fall back to looking up by product directly.
      const productSales = sales.filter(s => s.player !== null
        && (Number(s.product) === Number(product.id) || Number(s.price_forecast) === Number(pf.id)));
      const directId = sales_clients["Direct Sales"];
      const distId = sales_clients["Distributors"];
      const hasDirectSales = productSales.some(s => Number(s.client) === Number(directId));
      const hasDistributors = productSales.some(s => Number(s.client) === Number(distId));

      return {
        id: product.id,
        name: product.name,
        price,
        forecast,
        revenue,
        hasDirectSales,
        hasDistributors,
      };
    });
  }

  // Donut data: one slice per marketing client with spend > 0, colored
  // by the per-client palette so the chart visually echoes the sub-
  // category bullets in the breakdown list. Traditional first, then
  // Digital. Respects the Promotion Spend Breakdown product filter so
  // selecting a product narrows the donut to that product's promos.
  // When nothing has been spent yet, a single placeholder slice keeps
  // a gray ring rendered.
  get donutData() {
    const filter = this.state.promoBreakdownProductId;
    const items = [
      ...this.clientsByCategory(1, TRADITIONAL_PALETTE, filter),
      ...this.clientsByCategory(2, DIGITAL_PALETTE, filter),
    ].filter(item => item.spend > 0);
    if (items.length === 0) {
      return [{ name: 'No spend yet', value: 1, placeholder: true }];
    }
    return items.map(item => ({
      name: item.name,
      value: item.spend,
      color: item.color,
    }));
  }

  render() {
    // Lock the page for students only when no round has ever been
    // started. Does not fire for finished games (see ceo-decisions.js
    // for the full explanation).
    const { rounds, rounds_loaded } = this.props;
    const hasEverStarted = (rounds || []).some(
      r => r && r.is_started === true
    );
    if (rounds_loaded && !hasEverStarted) {
      return <RoundNotStartedGate />;
    }

    const actionsAllowed = this.actionsAllowed;
    const {
      isOwnRole,
      accountsReceivable,
      showNoProductsModal,
      isPreChecked,
      successMessage,
      showMakeDecisionsModal,
    } = this.state;
    const {
      location,
      router,
      currentRound: round,
    } = this.props;

    const fmtMoney = v => `$${Math.round(Math.abs(v)).toLocaleString('en-US')}`;
    const fmtMoneyM = v => `$${(Math.abs(v) / 1e6).toFixed(2)}M`;

    return (
      <OpsStyled.BodyWrapper>
        <LastStepModal
          location={location}
          router={router}
          url={`/decisions/product-marketing?round=${round}`}
        />
        <OpsStyled.TitleWrapper>
          <OpsStyled.Title id="product-marketing-decisions-title">
            Product Marketing
            {isOwnRole && <RoleButtons type="product_marketing" />}
          </OpsStyled.Title>
          {actionsAllowed === true && this.state.loaded === true && (
            <SubmitDecisionsButton
              id="product-marketing-submit-decisions"
              tooltipPlacement="bottom"
              data={this.state.submission_data.pm}
              isLoading={this.state.submitDecisionsLoading}
              onClick={this.submitDecisions}
              buttonComponent={OpsStyled.SubmitDecisionButton}
              isPreChecked={isPreChecked}
            />
          )}
        </OpsStyled.TitleWrapper>
        <OpsStyled.Subtitle>
          Set the product price, then choose which promotional and sales channels to reach customers, along with how much to invest in each.
        </OpsStyled.Subtitle>

        {actionsAllowed === true && this.state.loaded === true && (() => {
          const hasProducts = (this.state.products && this.state.products.length > 0)
            || (this.state.price_forecasts && this.state.price_forecasts.length > 0);
          return (
            <FinanceStyled.ManageButtonBar style={{ marginTop: 20 }}>
              <MakeDecisionsTooltipWrapper
                style={!hasProducts ? { cursor: 'not-allowed' } : undefined}
              >
                <FinanceStyled.ManageButton
                  onClick={this.openMakeDecisionsModal}
                  disabled={!hasProducts}
                  style={!hasProducts ? { opacity: 0.6, pointerEvents: 'none' } : undefined}
                >
                  Make Decisions
                </FinanceStyled.ManageButton>
                {!hasProducts && (
                  <MakeDecisionsTooltip className="make-decisions-tooltip">
                    No products exist yet. Coordinate with Product Development to create a product first.
                  </MakeDecisionsTooltip>
                )}
              </MakeDecisionsTooltipWrapper>
            </FinanceStyled.ManageButtonBar>
          );
        })()}

        {this.state.roleAlert && (
          <ErrorAlert
            title="Error | Not Authorized"
            message="You are not authorized to make decisions for this role."
            onHide={this.hideRoleAlert}
          />
        )}
        {showNoProductsModal && (
          <ErrorAlert
            title="Warning"
            message="Product Development has not created a product for you to price. Please coordinate with your Head of Product (or CEO) to develop a product first."
            onHide={this.hideNoProductsModal}
          />
        )}
        {successMessage && <SuccessAlert message={successMessage} />}

        {/* Existing modal hosts kept mounted off-screen so the
            EventEmitter-driven Set Pricing / Set Promo Budget /
            Set Sales Channel modals continue to open. */}
        <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', visibility: 'hidden' }} aria-hidden="true">
          <PMDecisionsRevenueForecast
            hasAccess={this.state.hasAccess}
            showRoleAlert={this.showRoleAlert}
            products={this.state.products}
            price_forecasts={this.state.price_forecasts}
            reloadData={this.fetchData}
            actionsAllowed={actionsAllowed}
            countOfProducts={this.countOfProducts}
            tourMode={this.props.tourMode}
            getSubmissionData={this.getSubmissionData}
            currentRound={this.props.currentRound}
            roundInUrl={this.props.roundInUrl}
            rounds={this.props.rounds}
            showSuccessMessage={this.showSuccessMessage}
          />
          <PMDecisionsPromotionalSpend
            marketing_clients={this.state.marketing_clients}
            price_forecasts={this.state.price_forecasts}
            products={this.state.products}
            promos={this.state.promos}
            hasAccess={this.state.hasAccess}
            showRoleAlert={this.showRoleAlert}
            reloadData={this.fetchData}
            actionsAllowed={actionsAllowed}
            countOfProducts={this.countOfProducts}
            tourMode={this.props.tourMode}
            getSubmissionData={this.getSubmissionData}
            currentRound={this.props.currentRound}
            roundInUrl={this.props.roundInUrl}
            rounds={this.props.rounds}
            accountsReceivable={accountsReceivable}
            showSuccessMessage={this.showSuccessMessage}
          />
          <PMDecisionsPlacement
            sales_clients={this.state.sales_clients}
            hasAccess={this.state.hasAccess}
            showRoleAlert={this.showRoleAlert}
            price_forecasts={this.state.price_forecasts}
            products={this.state.products}
            sales={this.state.sales}
            reloadData={this.fetchData}
            actionsAllowed={actionsAllowed}
            countOfProducts={this.countOfProducts}
            tourMode={this.props.tourMode}
            getSubmissionData={this.getSubmissionData}
            accountsReceivable={accountsReceivable}
            showSuccessMessage={this.showSuccessMessage}
          />
        </div>

        {this.state.loaded === false && (
          <Loader type="ball-scale-ripple" active />
        )}

        {this.state.loaded === true && (
          <div>
            <FinanceStyled.KpiRow cardCount={4}>
              <FinanceStyled.KpiCard>
                <FinanceStyled.KpiLabel>Promotional Spend</FinanceStyled.KpiLabel>
                <FinanceStyled.KpiValue>
                  {fmtMoney(this.totalPromoSpend)}
                </FinanceStyled.KpiValue>
              </FinanceStyled.KpiCard>
              <FinanceStyled.KpiCard>
                <FinanceStyled.KpiLabel>Average Selling Price</FinanceStyled.KpiLabel>
                <FinanceStyled.KpiValue>
                  ${this.avgPrice.toFixed(2)}
                </FinanceStyled.KpiValue>
              </FinanceStyled.KpiCard>
              <FinanceStyled.KpiCard>
                <FinanceStyled.KpiLabel>Placement Costs</FinanceStyled.KpiLabel>
                <FinanceStyled.KpiValue>
                  {fmtMoney(this.sumPlacement)}
                </FinanceStyled.KpiValue>
              </FinanceStyled.KpiCard>
              {/* Spans the last two grid columns so the "Total Marketing
                  Spending" label fits on a single line. The KpiRow grid
                  template is '1fr 1fr 1fr 0.7fr 1fr'; this card now
                  covers the trailing 0.7fr + 1fr columns. */}
              <FinanceStyled.KpiCard style={{ gridColumn: 'span 2' }}>
                <FinanceStyled.KpiLabel style={{ whiteSpace: 'nowrap' }}>
                  Total Marketing Spending
                </FinanceStyled.KpiLabel>
                <FinanceStyled.KpiValue style={{ color: '#C62828' }}>
                  {fmtMoney(this.totalMarketingSpend)}
                </FinanceStyled.KpiValue>
              </FinanceStyled.KpiCard>
            </FinanceStyled.KpiRow>

            {(() => {
              const showPromoBreakdown = this.totalPromoSpend > 0;
              const showPricing = this.avgPrice > 0;
              const showPlacement = this.sumPlacement > 0;
              if (!showPromoBreakdown && !showPricing && !showPlacement) {
                return null;
              }
              return (
                <OpsStyled.OpsLeadGrid>
                  {showPromoBreakdown && (
                    <OpsStyled.OpsLeadGridMarket>
                      <FinanceStyled.SectionCard>
                        <FinanceStyled.SectionHeader>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 12,
                            flexWrap: 'wrap',
                          }}>
                            <FinanceStyled.SectionTitle>Promotion Spend Breakdown</FinanceStyled.SectionTitle>
                            {this.renderPromoBreakdownFilters()}
                          </div>
                          <FinanceStyled.SectionSubtitle>
                            How your promotional dollars are split across channels.
                          </FinanceStyled.SectionSubtitle>
                        </FinanceStyled.SectionHeader>
                        {this.renderPromoBreakdown()}
                      </FinanceStyled.SectionCard>
                    </OpsStyled.OpsLeadGridMarket>
                  )}
                  {(showPricing || showPlacement) && (
                    <OpsStyled.OpsLeadGridRight>
                      {showPricing && (
                        <FinanceStyled.SectionCard>
                          <FinanceStyled.SectionHeader>
                            <FinanceStyled.SectionTitle>Pricing & Forecast</FinanceStyled.SectionTitle>
                          </FinanceStyled.SectionHeader>
                          {this.renderPricingTable()}
                        </FinanceStyled.SectionCard>
                      )}
                      {showPlacement && (
                        <FinanceStyled.SectionCard style={{ marginTop: showPricing ? 16 : 0 }}>
                          <FinanceStyled.SectionHeader>
                            <FinanceStyled.SectionTitle>Placement</FinanceStyled.SectionTitle>
                          </FinanceStyled.SectionHeader>
                          {this.renderPlacementTable()}
                        </FinanceStyled.SectionCard>
                      )}
                    </OpsStyled.OpsLeadGridRight>
                  )}
                </OpsStyled.OpsLeadGrid>
              );
            })()}
          </div>
        )}

        {this.state.showSetPricingPage && (
          <SetPricingPage
            products={this.state.products}
            price_forecasts={this.state.price_forecasts}
            currentRound={this.roundNumber}
            rounds={this.state.rounds}
            reloadData={this.fetchData}
            actionsAllowed={actionsAllowed}
            onClose={this.closeSetPricingPage}
            onFirstPricingSet={this.handleFirstPricingSet}
          />
        )}

        <FirstDecisionModal
          open={this.state.firstDecisionModalOpen}
          title="Nice work on your first pricing decision!"
          body="You can update this any time before the round deadline. Make sure every product has a price, then complete the other two marketing decisions before the round ends: product promotion and sales channel."
          ctaLabel="View Other Decisions"
          onCta={this.handleFirstDecisionCta}
          onClose={this.handleFirstDecisionClose}
        />

        <FirstDecisionModal
          open={this.state.firstPromoModalOpen}
          title="Congrats on making your first promo decision!"
          body="The last marketing decision you need to make is choosing the sales channel for your product."
          ctaLabel="View Sales Channel"
          onCta={this.handleFirstPromoModalCta}
          onClose={this.handleFirstPromoModalClose}
        />

        {this.state.showManagePromotionPage && (
          <ManagePromotionPage
            marketing_clients={this.state.marketing_clients_list}
            promos={this.state.promos}
            products={this.state.products}
            currentRound={this.roundNumber}
            rounds={this.state.rounds}
            reloadData={this.fetchData}
            getSubmissionData={this.getSubmissionData}
            showSuccessMessage={this.showSuccessMessage}
            actionsAllowed={actionsAllowed}
            onClose={this.closeManagePromotionPage}
            onFirstPromoSet={this.handleFirstPromoSet}
          />
        )}

        {this.state.showManagePlacementPage && (
          <ManagePlacementPage
            products={this.state.products}
            sales={this.state.sales}
            sales_clients={this.state.sales_clients_list}
            currentRound={this.roundNumber}
            rounds={this.state.rounds}
            reloadData={this.fetchData}
            actionsAllowed={actionsAllowed}
            onClose={this.closeManagePlacementPage}
          />
        )}

        {showMakeDecisionsModal && (() => {
          const pricingEnabled = this.state.products.length > 0
            || this.state.price_forecasts.length > 0;
          const promotionsEnabled = !this.isSetPromoBudgetDisabled;
          const placementEnabled = !this.isSetSalesChannelDisabled;
          return (
            <FinanceStyled.ManageOverlay onClick={this.closeMakeDecisionsModal}>
              <FinanceStyled.ManageModal onClick={e => e.stopPropagation()} style={{ maxWidth: 760 }}>
                <FinanceStyled.ManageModalTitle>Make Decisions</FinanceStyled.ManageModalTitle>
                <FinanceStyled.ManageModalSubtitle>Select a decision to explore</FinanceStyled.ManageModalSubtitle>
                <OpsStyled.DecisionFlowRow>
                  <OpsStyled.DecisionOptionWrapper>
                    <FinanceStyled.ManageOption
                      onClick={pricingEnabled ? this.pickMakeDecisionsOption('pricing') : undefined}
                      disabled={!pricingEnabled}
                    >
                      Set Pricing
                    </FinanceStyled.ManageOption>
                    <OpsStyled.DecisionOptionTooltip>
                      {pricingEnabled
                        ? 'Set the selling price and forecast for each product.'
                        : 'No products available yet. Coordinate with Product Development first.'}
                    </OpsStyled.DecisionOptionTooltip>
                  </OpsStyled.DecisionOptionWrapper>
                  <OpsStyled.DecisionFlowArrow>→</OpsStyled.DecisionFlowArrow>
                  <OpsStyled.DecisionOptionWrapper>
                    <FinanceStyled.ManageOption
                      onClick={promotionsEnabled ? this.pickMakeDecisionsOption('promotions') : undefined}
                      disabled={!promotionsEnabled}
                    >
                      Manage Promotions
                    </FinanceStyled.ManageOption>
                    <OpsStyled.DecisionOptionTooltip>
                      {promotionsEnabled
                        ? 'Split your marketing budget across traditional and digital channels.'
                        : 'Set pricing for at least one product before allocating promotional spend.'}
                    </OpsStyled.DecisionOptionTooltip>
                  </OpsStyled.DecisionOptionWrapper>
                  <OpsStyled.DecisionFlowArrow>→</OpsStyled.DecisionFlowArrow>
                  <OpsStyled.DecisionOptionWrapper>
                    <FinanceStyled.ManageOption
                      onClick={placementEnabled ? this.pickMakeDecisionsOption('placement') : undefined}
                      disabled={!placementEnabled}
                    >
                      Manage Placement
                    </FinanceStyled.ManageOption>
                    <OpsStyled.DecisionOptionTooltip>
                      {placementEnabled
                        ? 'Choose the sales channel for each priced product.'
                        : 'Set pricing for at least one product before choosing a sales channel.'}
                    </OpsStyled.DecisionOptionTooltip>
                  </OpsStyled.DecisionOptionWrapper>
                </OpsStyled.DecisionFlowRow>
                <FinanceStyled.ManageCloseRow>
                  <FinanceStyled.ManageCloseButton onClick={this.closeMakeDecisionsModal}>
                    Cancel
                  </FinanceStyled.ManageCloseButton>
                </FinanceStyled.ManageCloseRow>
              </FinanceStyled.ManageModal>
            </FinanceStyled.ManageOverlay>
          );
        })()}
      </OpsStyled.BodyWrapper>
    );
  }

  // Pill-style filter buttons next to the Promotion Spend Breakdown
  // header. "Total" defaults; product pills are added one per team
  // product (none rendered if the team has no products yet).
  renderPromoBreakdownFilters() {
    const { products } = this.state;
    const filter = this.state.promoBreakdownProductId;
    const baseStyle = {
      padding: '5px 12px',
      borderRadius: 999,
      fontFamily: "'Mukta Vaani', Lato, sans-serif",
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'border-color 0.15s ease, color 0.15s ease, background 0.15s ease',
      border: '1.5px solid #E5E9F0',
      background: '#F5F7FB',
      color: 'rgba(0, 27, 59, 0.6)',
    };
    const activeStyle = {
      ...baseStyle,
      background: '#FFFFFF',
      borderColor: '#3A507B',
      color: '#3A507B',
      boxShadow: '0 2px 6px rgba(58, 80, 123, 0.18)',
    };
    const renderPill = (id, label) => (
      <button
        key={`promo-pill-${id}`}
        type="button"
        onClick={this.selectPromoBreakdownProduct(id)}
        style={filter === id || Number(filter) === Number(id) ? activeStyle : baseStyle}
      >
        {label}
      </button>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {renderPill('all', 'Total')}
        {(products || []).map(p => renderPill(p.id, p.name))}
      </div>
    );
  }

  renderPromoBreakdown() {
    const filter = this.state.promoBreakdownProductId;
    const trad = this.sumByCategory(1, filter);
    const digi = this.sumByCategory(2, filter);
    const total = trad + digi;
    const tradColor = TRADITIONAL_COLOR;
    const digiColor = DIGITAL_COLOR;
    const tradItems = this.clientsByCategory(1, TRADITIONAL_PALETTE, filter);
    const digiItems = this.clientsByCategory(2, DIGITAL_PALETTE, filter);
    const fmtMoney = v => `$${Math.round(v).toLocaleString('en-US')}`;
    const placeholderColor = '#E1E5EA';

    return (
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start' }}>
        <div style={{ position: 'relative' }}>
          <ResponsiveContainer width="100%" height={240} debounce={500}>
            <PieChart>
              <Tooltip
                formatter={(val, name, entry) =>
                  entry && entry.payload && entry.payload.placeholder
                    ? ['-', '']
                    : [fmtMoney(val), name]
                }
              />
              <Pie
                data={this.donutData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={total > 0 ? 2 : 0}
                dataKey="value"
              >
                {this.donutData.map((d, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={d.placeholder ? placeholderColor : (d.color || tradColor)}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div style={{
            position: 'absolute',
            top: '50%', left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}>
            <div style={{
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
              fontSize: 12,
              color: 'rgba(0, 27, 59, 0.6)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>Total</div>
            <div style={{
              fontFamily: "'Mukta Vaani', Lato, sans-serif",
              fontSize: 18,
              fontWeight: 700,
              color: 'rgba(0, 27, 59, 0.94)',
            }}>{fmtMoney(total)}</div>
          </div>
        </div>

        <div>
          {this.renderBreakdownGroup('Traditional Marketing', trad, tradColor, tradItems, total)}
          <div style={{ height: 12 }} />
          {this.renderBreakdownGroup('Digital Marketing', digi, digiColor, digiItems, total)}
        </div>
      </div>
    );
  }

  // `total` here is the category total (digital or traditional). The
  // grand total `grandTotal` is the full spend across both categories
  // — used to compute the per-channel "% of total spending" hint
  // shown next to each sub-category name.
  renderBreakdownGroup(label, total, color, subcategories, grandTotal) {
    const fmt = v => `$${Math.round(v).toLocaleString('en-US')}`;
    const fmtPct = v => grandTotal > 0
      ? `${((v / grandTotal) * 100).toFixed(1)}%`
      : '0.0%';
    return (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
        }}>
          <span style={{
            display: 'inline-block',
            width: 10, height: 10,
            borderRadius: '50%',
            background: color,
          }} />
          <span style={{
            flex: 1,
            fontFamily: "'Mukta Vaani', Lato, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            color: 'rgba(0, 27, 59, 0.94)',
          }}>{label}</span>
          <span style={{
            fontFamily: "'Mukta Vaani', Lato, sans-serif",
            fontWeight: 700,
            fontSize: 14,
            color: 'rgba(0, 27, 59, 0.94)',
          }}>{fmt(total)}</span>
        </div>
        <ul style={{
          margin: 0,
          padding: '0 0 0 22px',
          listStyle: 'none',
          fontFamily: "'Mukta Vaani', Lato, sans-serif",
          fontSize: 13,
          lineHeight: '20px',
        }}>
          {subcategories.map(sub => (
            <li
              key={sub.id != null ? sub.id : sub.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: 'rgba(0, 27, 59, 0.7)',
              }}
            >
              <span style={{
                display: 'inline-block',
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: sub.color || 'rgba(0, 27, 59, 0.35)',
                flexShrink: 0,
              }} />
              <span style={{ flex: 1 }}>{sub.name}</span>
              <span style={{
                fontVariantNumeric: 'tabular-nums',
              }}>{fmtPct(sub.spend || 0)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderPricingTable() {
    const rows = this.productsRows;
    const fmt = v => `$${Math.round(v).toLocaleString('en-US')}`;

    if (rows.length === 0) {
      return (
        <FinanceStyled.SummaryTable>
          <tbody>
            <FinanceStyled.SummaryRow>
              <FinanceStyled.SummaryCell>&mdash;</FinanceStyled.SummaryCell>
            </FinanceStyled.SummaryRow>
          </tbody>
        </FinanceStyled.SummaryTable>
      );
    }

    return (
      <FinanceStyled.SummaryTable>
        <thead>
          <FinanceStyled.SummaryHeadRow>
            <FinanceStyled.SummaryHeadCell>Product</FinanceStyled.SummaryHeadCell>
            <FinanceStyled.SummaryHeadCell align="center">Price</FinanceStyled.SummaryHeadCell>
            <FinanceStyled.SummaryHeadCell align="center">Unit Forecast</FinanceStyled.SummaryHeadCell>
            <FinanceStyled.SummaryHeadCell align="center">Expected Revenue</FinanceStyled.SummaryHeadCell>
          </FinanceStyled.SummaryHeadRow>
        </thead>
        <tbody>
          {rows.map(row => (
            <FinanceStyled.SummaryRow key={`price-row-${row.id}`}>
              <FinanceStyled.SummaryCell>{row.name}</FinanceStyled.SummaryCell>
              <FinanceStyled.SummaryCell align="center">
                ${row.price.toFixed(2)}
              </FinanceStyled.SummaryCell>
              <FinanceStyled.SummaryCell align="center">
                {Math.round(row.forecast).toLocaleString('en-US')}
              </FinanceStyled.SummaryCell>
              <FinanceStyled.SummaryCell align="center">
                {fmt(row.revenue)}
              </FinanceStyled.SummaryCell>
            </FinanceStyled.SummaryRow>
          ))}
        </tbody>
      </FinanceStyled.SummaryTable>
    );
  }

  renderPlacementTable() {
    const rows = this.productsRows;
    if (rows.length === 0) {
      return (
        <FinanceStyled.SummaryTable>
          <tbody>
            <FinanceStyled.SummaryRow>
              <FinanceStyled.SummaryCell>&mdash;</FinanceStyled.SummaryCell>
            </FinanceStyled.SummaryRow>
          </tbody>
        </FinanceStyled.SummaryTable>
      );
    }
    const checkmark = (
      <span style={{ color: '#2DA548', fontWeight: 700, fontSize: 16 }}>✓</span>
    );
    const dash = <span style={{ color: 'rgba(0, 27, 59, 0.35)' }}>-</span>;
    return (
      <FinanceStyled.SummaryTable>
        <thead>
          <FinanceStyled.SummaryHeadRow>
            <FinanceStyled.SummaryHeadCell>Product</FinanceStyled.SummaryHeadCell>
            <FinanceStyled.SummaryHeadCell align="center">Direct Sales</FinanceStyled.SummaryHeadCell>
            <FinanceStyled.SummaryHeadCell align="center">Distributor</FinanceStyled.SummaryHeadCell>
          </FinanceStyled.SummaryHeadRow>
        </thead>
        <tbody>
          {rows.map(row => (
            <FinanceStyled.SummaryRow key={`place-row-${row.id}`}>
              <FinanceStyled.SummaryCell>{row.name}</FinanceStyled.SummaryCell>
              <FinanceStyled.SummaryCell align="center">
                {row.hasDirectSales ? checkmark : dash}
              </FinanceStyled.SummaryCell>
              <FinanceStyled.SummaryCell align="center">
                {row.hasDistributors ? checkmark : dash}
              </FinanceStyled.SummaryCell>
            </FinanceStyled.SummaryRow>
          ))}
        </tbody>
      </FinanceStyled.SummaryTable>
    );
  }
}

export default view(PMDecisions);



// WEBPACK FOOTER //
// ./src/containers/PMDecisions.js